/**
 * The catalogue of column types the Add column dialog offers, grouped the way
 * the rail lists them.
 *
 * Order is meaningful in both directions: the rail follows `COLUMN_GROUPS`, and
 * each group's grid follows its `options`. Adding a type is one entry here —
 * the rail, its count and the grid all read from this table.
 *
 * `briefs` holds the types that only resolve against a brief. Custom trackers
 * pass those to the dialog as excluded, which empties the group and drops it
 * from the rail.
 */
import {
  BadgeCheck,
  Building2,
  Calendar,
  CalendarRange,
  CircleDot,
  FileText,
  FolderOpen,
  GanttChart,
  Hash,
  Layers,
  Link,
  MousePointerClick,
  Paperclip,
  Percent,
  Sigma,
  SquareCheck,
  Type as TypeIcon,
  UsersRound,
} from 'lucide-react';
import type { ParseKeys } from 'i18next';

import type { CustomColumnType } from './useTrackersBoard';

/**
 * The column types that ARE the asset-approvals surface: the approval cell, and
 * the assets cell, which reads `approved/total` and links into the folders tree.
 * `hide_asset_approvals` drops both from the picker and from the rendered board.
 */
export const APPROVAL_COLUMN_TYPES: readonly CustomColumnType[] = ['approval', 'assets'];

export interface ColumnTypeOption {
  type: CustomColumnType;
  labelKey: ParseKeys<'trackers'>;
  descriptionKey: ParseKeys<'trackers'>;
  Icon: typeof TypeIcon;
}

export interface ColumnGroup {
  id: string;
  labelKey: ParseKeys<'trackers'>;
  Icon: typeof TypeIcon;
  options: ColumnTypeOption[];
}

export const COLUMN_GROUPS: readonly ColumnGroup[] = [
  {
    id: 'essentials',
    labelKey: 'trackersBoard.addColumnModal.essentials',
    Icon: TypeIcon,
    options: [
      {
        type: 'text',
        labelKey: 'trackersBoard.addColumnModal.text',
        descriptionKey: 'trackersBoard.addColumnModal.plainFreeFormText',
        Icon: TypeIcon,
      },
      {
        type: 'status',
        labelKey: 'trackersBoard.addColumnModal.status',
        descriptionKey: 'trackersBoard.addColumnModal.colouredLabelFromAPreset',
        Icon: CircleDot,
      },
      {
        type: 'checkbox',
        labelKey: 'trackersBoard.addColumnModal.checkbox',
        descriptionKey: 'trackersBoard.addColumnModal.aDoneNotDoneToggle',
        Icon: SquareCheck,
      },
    ],
  },
  {
    id: 'dates',
    labelKey: 'trackersBoard.addColumnModal.dates',
    Icon: Calendar,
    options: [
      {
        type: 'date',
        labelKey: 'trackersBoard.addColumnModal.date',
        descriptionKey: 'trackersBoard.addColumnModal.aSingleCalendarDate',
        Icon: Calendar,
      },
      {
        type: 'date_range',
        labelKey: 'trackersBoard.addColumnModal.dateRange',
        descriptionKey: 'trackersBoard.addColumnModal.startAndEndDatesWith',
        Icon: CalendarRange,
      },
      {
        type: 'timeline',
        labelKey: 'trackersBoard.addColumnModal.timeline',
        descriptionKey: 'trackersBoard.addColumnModal.phaseBarsAcrossASchedule',
        Icon: GanttChart,
      },
    ],
  },
  {
    id: 'people',
    labelKey: 'trackersBoard.addColumnModal.peopleGroup',
    Icon: UsersRound,
    options: [
      {
        type: 'people',
        labelKey: 'trackersBoard.addColumnModal.people',
        descriptionKey: 'trackersBoard.addColumnModal.assignWorkspaceMembers',
        Icon: UsersRound,
      },
      {
        type: 'team',
        labelKey: 'trackersBoard.addColumnModal.team',
        descriptionKey: 'trackersBoard.addColumnModal.nameTheTeamOnTheWork',
        Icon: Building2,
      },
    ],
  },
  {
    id: 'numbers',
    labelKey: 'trackersBoard.addColumnModal.numbers',
    Icon: Hash,
    options: [
      {
        type: 'number',
        labelKey: 'trackersBoard.addColumnModal.number',
        descriptionKey: 'trackersBoard.addColumnModal.numericValues',
        Icon: Hash,
      },
      {
        type: 'progress',
        labelKey: 'trackersBoard.addColumnModal.progress',
        descriptionKey: 'trackersBoard.addColumnModal.completionAsAPercentage',
        Icon: Percent,
      },
      {
        type: 'formula',
        labelKey: 'trackersBoard.addColumnModal.formula',
        descriptionKey: 'trackersBoard.addColumnModal.calculatedFromOtherColumns',
        Icon: Sigma,
      },
    ],
  },
  {
    id: 'media',
    labelKey: 'trackersBoard.addColumnModal.mediaAndLinks',
    Icon: Link,
    options: [
      {
        type: 'link',
        labelKey: 'trackersBoard.addColumnModal.link',
        descriptionKey: 'trackersBoard.addColumnModal.aClickableUrl',
        Icon: Link,
      },
      {
        type: 'files',
        labelKey: 'trackersBoard.addColumnModal.files',
        descriptionKey: 'trackersBoard.addColumnModal.linkDocumentsOrImages',
        Icon: Paperclip,
      },
    ],
  },
  {
    id: 'actions',
    labelKey: 'trackersBoard.addColumnModal.actions',
    Icon: MousePointerClick,
    options: [
      {
        type: 'button',
        labelKey: 'trackersBoard.addColumnModal.button',
        descriptionKey: 'trackersBoard.addColumnModal.runsAnAutomationOnClick',
        Icon: MousePointerClick,
      },
      {
        type: 'approval',
        labelKey: 'trackersBoard.addColumnModal.approval',
        descriptionKey: 'trackersBoard.addColumnModal.requestASignOffFrom',
        Icon: BadgeCheck,
      },
    ],
  },
  {
    id: 'briefs',
    labelKey: 'trackersBoard.addColumnModal.briefs',
    Icon: FileText,
    options: [
      {
        type: 'brief_type',
        labelKey: 'trackersBoard.addColumnModal.briefType',
        descriptionKey: 'trackersBoard.addColumnModal.showsEachBriefSType',
        Icon: FileText,
      },
      {
        type: 'project',
        labelKey: 'trackersBoard.addColumnModal.project',
        descriptionKey: 'trackersBoard.addColumnModal.showsEachBriefSProject',
        Icon: FolderOpen,
      },
      {
        type: 'assets',
        labelKey: 'trackersBoard.addColumnModal.projectAssets',
        descriptionKey: 'trackersBoard.addColumnModal.reviewApprovalsProgressForThe',
        Icon: Layers,
      },
    ],
  },
];
